import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { PhotoSelectionPhoto } from "../bottom-photo-menus/photo-selection-panel/photo";
import { SelectionBox } from "../selection-box";

import createJustifiedLayout from "justified-layout";
import { useResizeObserver } from "../../hooks/useResizeObserver";

import "./photo-grid.css";

export const PhotoGrid = ({
	photos,
	selectedPhotos = [],
	canSelect = false,
	targetRowHeight = 120,
	containerWidth: fixedContainerWidth = 0,
	onClick,
	getPhotoGestureHandlers,
	isDragging,
}: {
	photos: readonly eXpress.core.Photo[];
	selectedPhotos?: readonly eXpress.core.Photo[];
	canSelect?: boolean;
	targetRowHeight?: number;
	/**
	 * Added a fixed containerWidth so jest can work without divRef
	 * If specified, no auto measuring is done.
	 */
	containerWidth?: number;
	onClick?: (photo: eXpress.core.Photo) => void;
	getPhotoGestureHandlers?: (photo: eXpress.core.Photo) => Record<string, unknown>;
	isDragging?: boolean;
}) => {
	// Get container width
	// If it flickers, we can add a child div for adding the items to, and the parent div for width measuring
	const divRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(fixedContainerWidth);

	useLayoutEffect(() => {
		// Initial measurement
		if (!fixedContainerWidth && divRef.current) {
			const rect = divRef.current.getBoundingClientRect();
			setContainerWidth(rect.width);
		}
	}, [fixedContainerWidth]);

	useResizeObserver(divRef, (entry) => {
		if (!fixedContainerWidth) setContainerWidth(entry.width || 0);
	});

	// On mount, the divRef is still null and we don't know the width
	// To prevent flickering, we don't render the grid until we have a width
	const showGrid = containerWidth > 0;

	const { boxes, containerHeight } = useMemo(() => {
		return createJustifiedLayout(
			photos.map((p) => (p.orientedSize.width || 1) / (p.orientedSize.height || 1)),
			{
				containerPadding: 0,
				containerWidth,
				targetRowHeight,
				boxSpacing: 8,
			},
		);
	}, [photos, containerWidth, targetRowHeight]);

	return (
		<div ref={divRef} className="photo-grid" style={{ height: containerHeight }}>
			{showGrid &&
				photos.map((photo, index) => {
					const box = boxes[index];
					return (
						<div
							className="photo-grid__item"
							style={{
								position: "absolute",
								top: box.top,
								left: box.left,
								width: box.width,
								height: box.height,
							}}
							key={photo.id}
						>
							<SelectionBox
								canSelect={canSelect}
								isSelected={selectedPhotos.includes(photo)}
							>
								<PhotoSelectionPhoto
									photo={photo}
									className="photo-grid__photo"
									onClick={onClick ? () => onClick(photo) : undefined}
									onKeyPress={onClick ? () => onClick(photo) : undefined}
									isDragging={isDragging}
									{...(getPhotoGestureHandlers
										? getPhotoGestureHandlers(photo)
										: {})}
								/>
							</SelectionBox>
						</div>
					);
				})}
		</div>
	);
};
PhotoGrid.displayName = "PhotoGrid";
